import {Tr} from "@/i18n/i18n";
import Scene = Phaser.Scene;
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {SquareButton} from "@/scenes.ui/square-button";
import {Fonts} from "@/constants/font";
import {VerticalStandardPanel} from "@/scenes.ui/vertical-standard-panel";
import {Offer, OfferType} from "@/store/player/types";
import Sprite = Phaser.GameObjects.Sprite;
import Text = Phaser.GameObjects.Text;
import SpriteUtils from "@/utils/sprite-utils";

export class CampaignPanel extends VerticalStandardPanel {

    private lighting!: Sprite;

    private timeLabel!: Text;

    constructor(scene: Scene, canvas: HTMLCanvasElement, offer: Offer, shopCallback: () => unknown, onCloseCallback: () => unknown) {
        super(scene, canvas, Tr('spets.predlozhenie'));
        this.background.setAlpha(0.01);

        let mainText = TextUtils.addTextByFamily(scene, CampaignPanel.getMainText(offer),
            0, -174, '#ffc536', 21, 'normal', 380, Fonts.BENGALY_NORMAL);


        let light = SpriteUtils.addSprite(this.scene!, 0, -80, Keys.ATLAS_PROFILE, Keys.UI_ART_LIGHT, 4, 2);
        this.add(light);

        switch (offer.offerType) {
            case OfferType.ALL_CREATURES_DISCOUNT:
            case OfferType.FIRST_HERO:
                let creature1 = SpriteUtils.addSprite(this.scene!, -120, -80, Keys.ATLAS_CREATURES_BATTLE_1,  'elite-unicorn', 0.8);
                let creature2 = SpriteUtils.addSprite(this.scene!, -40, -80, Keys.ATLAS_CREATURES_BATTLE_1, 'elite-knight', 0.8);
                let creature3 = SpriteUtils.addSprite(this.scene!, 40, -80, Keys.ATLAS_CREATURES_BATTLE_1, 'elite-golem', 0.8);
                let creature4 = SpriteUtils.addSprite(this.scene!, 120, -80, Keys.ATLAS_CREATURES_BATTLE_1, 'elite-dragon-2', 0.8);
                this.add([creature1, creature2, creature3, creature4]);
                break;
            case OfferType.GEMS_X2:
                let gemsChest = SpriteUtils.addSprite(this.scene!, 0, -80, Keys.ATLAS_BANK, Keys.UI_SHOP_GEMS_7, 0.9);
                this.add(gemsChest);
                break;
            case OfferType.GREEN_DRAGON_DISCOUNT:
                this.loadAndSetSprite(scene, 'elite-dragon-3');
                break;
            case OfferType.BLACK_DRAGON_DISCOUNT:
                this.loadAndSetSprite(scene, 'myth-dragon-2');
                break;
            case OfferType.BRAND_DISCOUNT:
                this.loadAndSetSprite(scene, 'brand');
                break;
            case OfferType.MYTH_20:
                let mythText = Tr('discount.20percent.mythical.heroes');
                let mythTextLabel = TextUtils.addTextByFamily(
                    this.scene!,
                    mythText,
                    0,
                    -80,
                    '#ffc536',
                    18,
                    'normal',
                    350,
                    Fonts.BENGALY_NORMAL
                );
                this.add(mythTextLabel);
                break;
        }

        let hurryUpLabel = TextUtils.addTextAdvanced(scene, Tr('uspey.kupit'), 0, 4,
            '#ffffff', 16, 'normal', 350, 'center', 2, Fonts.ARIAL_MT);

        let tillEndLabel = TextUtils.addTextAdvanced(scene, Tr('do.kontsa.predlozhenija'), 0, 32,
            '#716F6A', 10, 'normal', 350, 'center', 2, Fonts.ARIAL_MT);

        this.timeLabel = TextUtils.addTextByFamily(scene, Tr('ch.m.12.59'),
            0, 60, '#ffc536', 16, 'normal', 400, Fonts.ARIAL_MT);

        let okButton = new SquareButton(this.scene, 0, 150, 0,
            Keys.UI_ART_GREEN_BUTTON, undefined, Keys.OFFER_SHOP_ICON, Tr('v.magazin'), false);

        this.add(okButton);

        let self = this;
        okButton.setCallback(() => {
            if (this.isOnTop()) {
                self.close();
                shopCallback();
            }
        });

        this.add([hurryUpLabel, tillEndLabel, mainText, this.timeLabel]);
    }

    private loadAndSetSprite(scene: Scene, creatureId: string) {
        let creaturePicture = SpriteUtils.addSprite(this.scene!, 0, -20, Keys.ATLAS_PROFILE, Keys.UI_SQUARE_FRAME, 0.25);
        this.add(creaturePicture);

        scene.load.image(Keys.profileCreature(creatureId), `assets/profile/creatures/${creatureId}.png`);
        scene.load.once(Phaser.Loader.Events.COMPLETE, () => {
            SpriteUtils.setSprite(scene, creaturePicture, Keys.profileCreature(creatureId))
        });
        scene.load.start();
    }

    public setTime(remainTime: string) {
        if (this.active && this.timeLabel && this.timeLabel.active) {
            this.timeLabel!.setText(remainTime);
        }
    }

    protected preInit(): void {
        this.lighting = SpriteUtils.addSprite(this.scene, 0, 0, Keys.OFFER_LIGHT_BACK, undefined, 4.6).setAlpha(0);
        this.add(this.lighting);

        this.scene!.tweens.add({
            targets: this.lighting,
            alpha: 1,
            duration: 500
        });

        this.scene!.tweens.add({
            targets: this.lighting,
            angle: 360,
            duration: 90000,
            loop: -1,
        });
    }

    protected preClose(): void {
        this.lighting.setVisible(false);
    }

    private static getMainText(offer: Offer): string {
        switch (offer.offerType) {
            case OfferType.FIRST_HERO:
                return Tr('skidka.na.pervogo.geroja').toUpperCase();
            case OfferType.GREEN_DRAGON_DISCOUNT:
            case OfferType.BLACK_DRAGON_DISCOUNT:
                return Tr('unikalniy.drakon.so.skidkoy').toUpperCase();
            case OfferType.BRAND_DISCOUNT:
                return Tr('br.camp').toUpperCase();
            case OfferType.ALL_CREATURES_DISCOUNT:
                return Tr('skidki.na.vseh.geroev.v.magazine');
            case OfferType.GEMS_X2:
                return Tr('kupi.izumrudi.i.poluchi..v.podarok');
        }
        return '';
    }
}
